'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ProductExcelImportForm, type ProductExcelImportFormRef } from '@/components/partials/products/excel-imports'
import { type CreateProductExcelImportParams } from '@/types/product-excel-import'
import { useCreateProductExcelImport } from '@/hooks'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const productExcelImportFormRef = useRef<ProductExcelImportFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { mutate: createProductExcelImport, isPending: isLoading } = useCreateProductExcelImport()

  const handleSubmit = (data: CreateProductExcelImportParams) => {
    createProductExcelImport(data, {
      onSuccess: async newProductExcelImport => {
        setIsRedirecting(true)
        // Set the data in a cache with the same staleTime as the query to prevent immediate refetch
        queryClient.setQueryData(['productExcelImport', newProductExcelImport.id], newProductExcelImport)
        // Prefetch to ensure the query is in a success state
        queryClient.setQueryDefaults(['productExcelImport', newProductExcelImport.id], {
          staleTime: 5 * 60 * 1000
        })
        await queryClient.invalidateQueries({ queryKey: ['productExcelImports'] })
        toast.success(t('created_successfully'))
        router.push(`/products/import/excel/${newProductExcelImport.id}`)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (productExcelImportFormRef.current) {
        productExcelImportFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/products/import/excel')
  }

  return (
    <ProductExcelImportForm
      ref={productExcelImportFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
